'use client'

import {
  type CardProps,
  Card,
} from '@snowball-tech/fractal/dist/src/components/Card'
import { cn } from '@snowball-tech/fractal/dist/src/styles/helpers'

import type { ComponentProps, CSSProperties, ReactNode } from 'react'
import { isMobile } from 'react-device-detect'
import reactHotToast, {
  type Toast as ReactHotToast,
  type ToastOptions as ReactHotToastOptions,
  ToastBar as ReactHotToastBar,
  Toaster as ReactHotToaster,
  resolveValue,
} from 'react-hot-toast'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isString from 'lodash/fp/isString'
import omit from 'lodash/fp/omit'

import type { ExtendedToast } from '../types'

import useTranslations from '../hooks/useTranslations'
import { spacings } from '../styles/helpers'

export { toast as reactHotToast } from 'react-hot-toast'

export type ToastContent =
  | Parameters<typeof reactHotToast.custom>[0]
  | ReactNode
export type ToastOptions = {
  title?: string
  type?: 'blank' | 'error' | 'info' | 'loading' | 'success' | 'warning'
} & ReactHotToastOptions
export function displayToast(
  content: ToastContent,
  { title, type, ...options }: ToastOptions,
) {
  return reactHotToast.custom(
    isString(content) || isFunction(content) ? content : <>{content}</>,
    {
      icon: type === 'warning' ? '⚠️' : type === 'info' ? 'ℹ️' : options.icon,
      title: isEmpty(title) ? undefined : title,
      type:
        type !== 'blank' &&
        type !== 'error' &&
        type !== 'loading' &&
        type !== 'success'
          ? 'blank'
          : isEmpty(type)
            ? undefined
            : type,
      ...(type === 'warning' ? omit(['icon'], options) : options),
    } as ToastOptions,
  )
}

type ToastTypeCallSignature = (
  content: ToastContent,
  titleOrOptions?: string | Omit<ToastOptions, 'type'>,
  options?: Omit<ToastOptions, 'title' | 'type'>,
) => string

export const toast: {
  (
    content: ToastContent,
    typeOrOptions?: ToastOptions | ToastOptions['type'],
    titleOrOptions?: Omit<ToastOptions, 'type'> | ToastOptions['title'],
    options?: Omit<ToastOptions, 'title' | 'type'>,
  ): string
  blank: ToastTypeCallSignature
  custom: typeof displayToast
  dismiss: typeof reactHotToast.dismiss
  error: ToastTypeCallSignature
  info: ToastTypeCallSignature
  loading: ToastTypeCallSignature
  promise: typeof reactHotToast.promise
  remove: typeof reactHotToast.remove
  success: ToastTypeCallSignature
  warn: ToastTypeCallSignature
  warning: ToastTypeCallSignature
} = (content, typeOrOptions, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    type: isString(typeOrOptions) ? typeOrOptions || 'blank' : 'blank',
    ...(isString(titleOrOptions)
      ? isString(typeOrOptions)
        ? options
        : typeOrOptions
      : titleOrOptions),
  })
toast.success = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'success',
  })
toast.warning = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'warning',
  })
toast.error = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'error',
  })
toast.loading = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'loading',
  })
toast.blank = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'blank',
  })
toast.custom = displayToast
toast.dismiss = reactHotToast.dismiss
toast.remove = reactHotToast.remove
toast.promise = reactHotToast.promise
toast.warn = toast.warning
toast.info = (content, titleOrOptions, options) =>
  displayToast(content, {
    title: isString(titleOrOptions) ? titleOrOptions : undefined,
    ...(isString(titleOrOptions) ? options : titleOrOptions),
    type: 'info',
  })

type Props = {
  contentClassName?: string
  contentStyle?: CSSProperties
  fontSize?: CardProps['fontSize']
  toastStyle?: CSSProperties
  onDismiss?: (toast: ReactHotToast) => void
} & ComponentProps<typeof ReactHotToaster>
export default function Toaster({
  containerStyle,
  contentClassName,
  contentStyle,
  fontSize = 2,
  gutter,
  onDismiss,
  position,
  toastStyle,
  ...props
}: Props) {
  const $t = useTranslations()

  const actualPosition = isEmpty(position)
    ? isMobile
      ? 'top-center'
      : 'top-right'
    : position

  return (
    <ReactHotToaster
      containerStyle={{
        left: spacings[4],
        right: spacings[4],
        top: spacings[12],
        ...containerStyle,
      }}
      gutter={gutter ?? spacings[2]}
      position={actualPosition}
      {...props}
    >
      {(t) => (
        <ReactHotToastBar style={{ padding: 0, ...toastStyle }} toast={t}>
          {({ icon, message }) => (
            <Card
              className={cn(
                'max-w-full',
                t.type === 'blank' && t.icon !== '⚠️' && t.icon !== 'ℹ️'
                  ? 'bg-white'
                  : '',
                props?.toastOptions?.className,
                props?.toastOptions?.[t.type]?.className,
                t.className,
              )}
              color={
                t.type === 'blank'
                  ? t.icon === '⚠️'
                    ? 'warning'
                    : t.icon === 'ℹ️'
                      ? 'blue'
                      : (t.type as CardProps['color'])
                  : (t.type as CardProps['color'])
              }
              dismissable={t.type !== 'loading'}
              dismissButtonLabel={$t('general.close')}
              fontSize={fontSize || 1}
              icon={icon}
              style={{
                ...props?.toastOptions?.style,
                ...props?.toastOptions?.[t.type]?.style,
                ...t.style,
              }}
              title={
                (t as ExtendedToast).title ||
                (icon
                  ? $t.notEmpty(
                      `toast.${t.type === 'blank' && t.icon === '⚠️' ? 'warning' : t.type}.title`,
                    )
                  : undefined)
              }
              onDismiss={() => {
                toast.dismiss(t.id)
                onDismiss?.(t)
              }}
            >
              <div
                className={cn('[&>div]:m-0 [&>div]:block', contentClassName)}
                style={{ ...contentStyle }}
              >
                {resolveValue(message, t)}
              </div>
            </Card>
          )}
        </ReactHotToastBar>
      )}
    </ReactHotToaster>
  )
}
